// src/pages/TacticBoardPage.jsx
// ─────────────────────────────────────────────────────────────────────────
// Sucrose Tactic — the coaching board.
//
// Owns the document (the playbook) and the view state around it; the canvas,
// timeline and panels are all controlled from here so there is one place where
// an edit becomes an undo entry and a keyframe.
// ─────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getHeroImageUrlWithFallback } from "../utils/heroCatalog";
import { createProjector } from "../utils/tactic/homography";
import {
  addPhase,
  createDrawing,
  createHeroToken,
  createPlaybook,
  createWardToken,
  hasKeyframeAt,
  mirrorPlaybook,
  playbookExtent,
  removeKeyframe,
  removePhase,
  sampleTokenPosition,
  upsertKeyframe,
} from "../utils/tactic/playbook";
import { summarizePlan } from "../utils/tactic/planSummary";
import { reviewTacticByAI } from "../utils/aiApi";
import {
  describeImageRow,
  startPositionFor,
  tokensFromGame,
  tokensFromImageRow,
} from "../utils/tactic/draftImport";
import { parseDraftImageByAI } from "../utils/aiApi";
import { buildHeroLaneIndex, primaryLaneFor } from "../utils/tactic/heroLanes";
import {
  deletePlaybook,
  exportAllFile,
  exportPlaybookFile,
  importFile,
  importPlaybooks,
  loadCorners,
  loadLandmarks,
  loadPlaybooks,
  loadTerrain,
  resetCorners,
  resetLandmarks,
  saveCorners,
  saveLandmarks,
  savePlaybook,
  saveTerrain,
} from "../utils/tactic/tacticStorage";
import {
  canCopyImage,
  copyBlobToClipboard,
  downloadBlob,
  renderBoardPng,
} from "../utils/tactic/boardExport";
import TacticCanvas, { IMG_H, IMG_W, TEAM_COLORS } from "../components/tactic/TacticCanvas";
import TacticTimeline from "../components/tactic/TacticTimeline";
import HeroPickerPanel from "../components/tactic/HeroPickerPanel";
import PlaybookPanel from "../components/tactic/PlaybookPanel";
import DraftImportPanel from "../components/tactic/DraftImportPanel";
import TacticReviewPanel from "../components/tactic/TacticReviewPanel";
import LanePickerDialog from "../components/tactic/LanePickerDialog";
import TeamScoutDialog from "../components/tactic/TeamScoutDialog";
import {
  ALL_TEAMS_KEY,
  LANE_OF_CLASS,
  gamesVsTeam,
  heroesOnSide,
  recordVsTeam,
  specterGamesForTeam,
  statsForCatalogName,
  teamsForPicker,
} from "../utils/tactic/teamScout";
// Same lazy All_Scrim loader the TierData matchup panel uses — one heavy parse,
// shared shape, so a win rate here matches a win rate there. The hook takes the
// parser, so the scouting sheet rides the same machinery.
import { useSeasonLog } from "../components/tier/useSeasonLog";
import { parseSpecterScrimP } from "../utils/excelParser";
import { Btn, Label, Panel, Row, T, Toggle, useMediaQuery } from "../components/tactic/tacticUi";

const TOOLS = [
  { id: "select", label: "เลือก/ลาก", icon: "✥" },
  { id: "pen", label: "วาดอิสระ", icon: "✎" },
  { id: "arrow", label: "ลูกศร", icon: "➜" },
  { id: "line", label: "เส้นตรง", icon: "／" },
  { id: "rect", label: "สี่เหลี่ยม", icon: "▭" },
  { id: "circle", label: "วงกลม", icon: "◯" },
  { id: "text", label: "ข้อความ", icon: "T" },
  { id: "marker", label: "เครื่องหมาย", icon: "◎" },
];

const COLORS = ["#ff1026", "#3b9dff", "#ff4d6d", "#34d399", "#facc15", "#ffffff"];

// Single-key tool switches, on the same letters every drawing app uses.
const MARK_LABELS = {
  pen: "เส้นวาดอิสระ",
  arrow: "ลูกศร",
  line: "เส้นตรง",
  rect: "สี่เหลี่ยม",
  circle: "วงกลม",
  text: "ข้อความ",
  marker: "เครื่องหมาย",
};

const TOOL_KEYS = {
  v: "select",
  p: "pen",
  a: "arrow",
  l: "line",
  r: "rect",
  c: "circle",
  t: "text",
  m: "marker",
};

const HISTORY_LIMIT = 60;

// One shared empty list, so "no games" is the same reference every render.
const NO_GAMES = [];

// Matches the canvas's group-drag clamp, so a nudge stops where a drag does.
const FIELD_PAD = 0.004;

export default function TacticBoardPage({ games, excelFile }) {
  const navigate = useNavigate();

  // ── Document ───────────────────────────────────────────────────────────
  const initialPlaybook = useMemo(() => createPlaybook(), []);
  const [playbook, setPlaybook] = useState(initialPlaybook);
  const [playbooks, setPlaybooks] = useState([]);

  // The document as it was last written to storage. "Unsaved" is then just
  // "not that object", which means undoing back to a saved state clears the
  // flag instead of leaving it stuck on.
  const [savedPlaybook, setSavedPlaybook] = useState(initialPlaybook);
  const dirty = playbook !== savedPlaybook;

  // ── View state ─────────────────────────────────────────────────────────
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [tool, setTool] = useState("select");
  const [drawColor, setDrawColor] = useState(COLORS[0]);
  const [drawWidth, setDrawWidth] = useState(5);
  const [bindMarksToTime, setBindMarksToTime] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedDrawingId, setSelectedDrawingId] = useState(null);
  const [addTeam, setAddTeam] = useState("blue");

  const [showLandmarks, setShowLandmarks] = useState(true);
  const [showPaths, setShowPaths] = useState(true);
  const [exporting, setExporting] = useState(false);
  const boardSvgRef = useRef(null);

  const [review, setReview] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [calibrating, setCalibrating] = useState(false);

  const [corners, setCorners] = useState(() => loadCorners());
  const [terrain, setTerrain] = useState(() => loadTerrain());
  const [landmarks, setLandmarks] = useState(() => loadLandmarks());
  const [landmarkMode, setLandmarkMode] = useState(false);
  const [toast, setToast] = useState(null);

  const projector = useMemo(() => createProjector(corners), [corners]);

  // Lane histogram from the uploaded workbook — drives the hero shelf's lane
  // filter and where a clicked hero lands.
  const laneIndex = useMemo(() => buildHeroLaneIndex(games), [games]);

  useEffect(() => {
    setPlaybooks(loadPlaybooks());
  }, []);

  const notify = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast((t) => (t === msg ? null : t)), 2600);
  }, []);

  // ── Mutation with undo ─────────────────────────────────────────────────
  //
  // Undo covers the playbook AND the traced terrain, because deleting a
  // polygon you spent a minute tracing is exactly the edit you most want back.
  // Each entry snapshots both; unchanged halves are shared by reference, so an
  // entry costs nothing when only one of them moved.
  //
  // Map corners are deliberately left out — calibration drags fire continuously
  // and would flood the stack. They have their own reset button.

  const playbookRef = useRef(playbook);
  const terrainRef = useRef(terrain);
  const past = useRef([]);
  const future = useRef([]);

  const record = useCallback(() => {
    past.current.push({ playbook: playbookRef.current, terrain: terrainRef.current });
    if (past.current.length > HISTORY_LIMIT) past.current.shift();
    future.current = [];
  }, []);

  // Everything here is synchronous and outside any state updater: React may
  // invoke an updater twice (StrictMode does in dev), which would otherwise
  // push the same edit onto the stack twice and make undo need two presses.
  const mutate = useCallback(
    (updater, { record: shouldRecord = true } = {}) => {
      const prev = playbookRef.current;
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (next === prev) return;
      if (shouldRecord) record();
      playbookRef.current = next;
      setPlaybook(next);
    },
    [record]
  );

  const mutateTerrain = useCallback(
    (updater) => {
      const prev = terrainRef.current;
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (next === prev) return;
      record();
      terrainRef.current = next;
      setTerrain(next);
      saveTerrain(next);
    },
    [record]
  );

  /** Swap the whole document — new plan, opened plan, or a fresh save stamp. */
  const replacePlaybook = useCallback((next, { saved = false, keepHistory = false } = {}) => {
    if (!keepHistory) {
      past.current = [];
      future.current = [];
    }
    playbookRef.current = next;
    setPlaybook(next);
    if (saved) setSavedPlaybook(next);
  }, []);

  /** One step along the history, in either direction. */
  const step = useCallback((from, to) => {
    const entry = from.current.pop();
    if (!entry) return;
    to.current.push({ playbook: playbookRef.current, terrain: terrainRef.current });

    if (entry.playbook !== playbookRef.current) {
      playbookRef.current = entry.playbook;
      setPlaybook(entry.playbook);
    }
    if (entry.terrain !== terrainRef.current) {
      terrainRef.current = entry.terrain;
      setTerrain(entry.terrain);
      saveTerrain(entry.terrain);
    }
  }, []);

  const undo = useCallback(() => step(past, future), [step]);
  const redo = useCallback(() => step(future, past), [step]);

  // ── Playback ───────────────────────────────────────────────────────────
  const timeRef = useRef(0);
  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  const duration = playbook.duration || 30;

  useEffect(() => {
    if (!playing) return undefined;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      const next = timeRef.current + dt * speed;
      if (next >= duration) {
        timeRef.current = duration;
        setTime(duration);
        setPlaying(false);
        return;
      }
      timeRef.current = next;
      setTime(next);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, duration]);

  // ── Selection ──────────────────────────────────────────────────────────
  //
  // "replace" — a plain click or a fresh rubber band
  // "toggle"  — shift-clicking one token in or out
  // "add"     — a shift-held rubber band, which only ever grows the selection
  // Tokens and marks are never selected at the same time — one Delete key has
  // to mean one thing.
  const selectTokens = useCallback((ids, mode = "replace") => {
    setSelectedDrawingId(null);
    setSelectedIds((prev) => {
      if (mode === "replace") return new Set(ids);
      const next = new Set(prev);
      for (const id of ids) {
        if (mode === "toggle" && next.has(id)) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }, []);

  const selectDrawing = useCallback((id) => {
    setSelectedIds(new Set());
    setSelectedDrawingId(id);
  }, []);

  // ── Token editing ──────────────────────────────────────────────────────

  // A drag fires a stream of moves; this tracks whether we are mid-gesture so
  // only the first one lands in the undo stack.
  const draggingRef = useRef(false);
  useEffect(() => {
    const stop = () => {
      draggingRef.current = false;
    };
    window.addEventListener("pointerup", stop);
    return () => window.removeEventListener("pointerup", stop);
  }, []);

  /**
   * Dragging records a keyframe at the playhead — auto-key. The canvas sends
   * the whole moved group at once so a five-hero rotation is one undo entry,
   * not five.
   */
  const handleTokensMove = useCallback(
    (moves) => {
      if (!moves?.length) return;
      const at = timeRef.current;
      const byId = new Map(moves.map((m) => [m.id, m]));

      mutate(
        (pb) => ({
          ...pb,
          tokens: pb.tokens.map((tok) => {
            const m = byId.get(tok.id);
            return m ? { ...tok, keys: upsertKeyframe(tok.keys, at, m.x, m.y) } : tok;
          }),
        }),
        { record: !draggingRef.current }
      );
      draggingRef.current = true;
    },
    [mutate]
  );

  /** Arrow-key equivalent of a group drag: same delta clamp, same auto-key. */
  const nudgeSelection = useCallback(
    (dx, dy) => {
      const at = timeRef.current;
      const moving = playbookRef.current.tokens.filter((tok) => selectedIds.has(tok.id));
      if (!moving.length) return;

      const spots = moving.map((tok) => sampleTokenPosition(tok, at));
      const cdx = Math.max(
        FIELD_PAD - Math.min(...spots.map((p) => p.x)),
        Math.min(1 - FIELD_PAD - Math.max(...spots.map((p) => p.x)), dx)
      );
      const cdy = Math.max(
        FIELD_PAD - Math.min(...spots.map((p) => p.y)),
        Math.min(1 - FIELD_PAD - Math.max(...spots.map((p) => p.y)), dy)
      );
      if (!cdx && !cdy) return;

      const ledger = new Map(
        moving.map((tok, i) => [
          tok.id,
          upsertKeyframe(tok.keys, at, spots[i].x + cdx, spots[i].y + cdy),
        ])
      );
      mutate((pb) => ({
        ...pb,
        tokens: pb.tokens.map((t) => (ledger.has(t.id) ? { ...t, keys: ledger.get(t.id) } : t)),
      }));
    },
    [mutate, selectedIds]
  );

  /**
   * Key or unkey every selected token at the playhead. Mixed selections resolve
   * towards adding — you only strip keys when all of them already sit on one,
   * which is what "the button says ลบคีย์เฟรม" has to mean.
   */
  const toggleKeyframes = useCallback(
    (ids) => {
      const targets = playbookRef.current.tokens.filter((t) => ids.includes(t.id));
      if (!targets.length) return;

      const at = timeRef.current;
      // A token must keep at least one position, so its only key is never a
      // removal candidate.
      const removable = targets.filter((t) => hasKeyframeAt(t.keys, at) && t.keys.length > 1);
      const addable = targets.filter((t) => !hasKeyframeAt(t.keys, at));
      const removing = !addable.length && removable.length > 0;
      const affected = removing ? removable : addable;
      if (!affected.length) return;

      const ledger = new Map(
        affected.map((tok) => {
          if (removing) return [tok.id, removeKeyframe(tok.keys, at)];
          const pos = sampleTokenPosition(tok, at);
          return [tok.id, upsertKeyframe(tok.keys, at, pos.x, pos.y)];
        })
      );

      mutate((pb) => ({
        ...pb,
        tokens: pb.tokens.map((t) => (ledger.has(t.id) ? { ...t, keys: ledger.get(t.id) } : t)),
      }));
    },
    [mutate]
  );

  // Clicking a hero opens the lane picker rather than placing straight away —
  // the board's guess is now the dialog's highlighted suggestion.
  const [pendingHero, setPendingHero] = useState(null);

  // Scouting sheet: which team we are reading, and whether the sheet is open.
  const [scoutTeam, setScoutTeam] = useState(ALL_TEAMS_KEY);
  const [scoutOpen, setScoutOpen] = useState(false);
  const [scoutPool, setScoutPool] = useState("played");
  const [scoutHero, setScoutHero] = useState("");
  const [scoutLane, setScoutLane] = useState("");
  // The season log is a heavy parse, so it waits until someone asks for numbers
  // — opening the sheet, choosing a team, or switching the shelf to ดูสถิติ.
  const [wantStats, setWantStats] = useState(false);
  // Which sheet the opponent's half reads: "us" (games against our team), "scouted"
  // (Specter_Scrim), or both. The coach asked for both at once by default.
  const [scoutSource, setScoutSource] = useState("both");
  // The stat sheet holds a handful of games per opponent — too thin to answer
  // "who does this hero beat". The season log holds the whole season, so that is
  // the default pool; it is parsed on the first open, not on page load.
  const [scoutScope, setScoutScope] = useState("season");
  const { log: seasonLog, loading: seasonLoading } = useSeasonLog(
    excelFile,
    scoutOpen || wantStats
  );
  // Specter_Scrim: the same teams watched playing everyone else. Cheap next to
  // All_Scrim, and loaded on the same trigger.
  const { log: specterLog, loading: specterLoading } = useSeasonLog(
    excelFile,
    scoutOpen || wantStats,
    parseSpecterScrimP
  );
  const specterRows = useMemo(() => specterLog?.games || NO_GAMES, [specterLog]);
  // Both of these feed useMemo deps that recount hundreds of games, so they have
  // to be stable references — a fresh [] every render would recount every render.
  const seasonGames = useMemo(() => seasonLog?.games || NO_GAMES, [seasonLog]);
  const scoutGames = useMemo(
    () => (scoutScope === "season" && seasonGames.length ? seasonGames : games || NO_GAMES),
    [scoutScope, seasonGames, games]
  );

  // Numbers for the shelf: both benches over the team on screen, looked up by
  // hero name. Built once per pool rather than per tile.
  const scoutTeams = useMemo(
    () => teamsForPicker(scoutGames, specterRows),
    [scoutGames, specterRows]
  );
  const scoutRecord = useMemo(() => recordVsTeam(scoutGames, scoutTeam), [scoutGames, scoutTeam]);
  const scouted = useMemo(
    () => specterGamesForTeam(specterRows, scoutTeam),
    [specterRows, scoutTeam]
  );
  const shelfStats = useMemo(() => {
    const pool = gamesVsTeam(scoutGames, scoutTeam);
    return {
      ours: new Map(heroesOnSide(pool, "fs").map((r) => [r.name, r])),
      // Their side reads our games against them plus the ones we only watched,
      // and keeps the split so the tooltip can show both.
      theirs: (() => {
        const vsUs = new Map(heroesOnSide(pool, "op").map((r) => [r.name, r.games]));
        return new Map(
          heroesOnSide([...pool, ...scouted], "op").map((r) => {
            const mine = vsUs.get(r.name) || 0;
            return [r.name, { ...r, vsUs: mine, watched: r.games - mine }];
          })
        );
      })(),
    };
  }, [scoutGames, scoutTeam, scouted]);

  // The shelf asks by catalog name and class, which is how a hero the sheet
  // keys under one name ("Flowborn") finds the form's own games.
  const statsOf = useCallback(
    (name, heroClass) => {
      const lane = LANE_OF_CLASS[heroClass] || "";
      const ours = statsForCatalogName(shelfStats.ours, name, lane);
      const theirs = statsForCatalogName(shelfStats.theirs, name, lane);
      if (!ours && !theirs) return null;
      return { ours, theirs, logName: ours?.logName || theirs?.logName || "", lane };
    },
    [shelfStats]
  );

  const openScout = useCallback((mode = "played", hero = "", lane = "") => {
    setWantStats(true);
    setScoutPool(mode);
    setScoutHero(hero);
    setScoutLane(lane);
    setScoutOpen(true);
  }, []);

  // A workbook without an All_Scrim log parses to nothing. Fall back to the
  // stat sheet rather than leaving "ทั้งซีซั่น" lit over the sheet's numbers.
  useEffect(() => {
    if (seasonLog && !seasonLog.games?.length) setScoutScope("current");
  }, [seasonLog]);

  const takenLanes = useMemo(
    () =>
      new Set(
        playbook.tokens.filter((tk) => tk.team === addTeam).map((tk) => tk.role).filter(Boolean)
      ),
    [playbook.tokens, addTeam]
  );

  const placeHero = useCallback(
    (heroName, lane, team) => {
      const [x, y] = lane
        ? startPositionFor(lane, team)
        : [team === "blue" ? 0.18 : 0.82, team === "blue" ? 0.18 : 0.82];

      const token = createHeroToken({ heroName, team, role: lane, x, y, t: 0 });
      mutate((pb) => ({ ...pb, tokens: [...pb.tokens, token] }));
      setSelectedIds(new Set([token.id]));
      setPendingHero(null);
    },
    [mutate]
  );

  const addWard = useCallback(() => {
    const token = createWardToken({
      team: addTeam,
      x: addTeam === "blue" ? 0.4 : 0.6,
      y: 0.5,
      t: 0,
    });
    mutate((pb) => ({ ...pb, tokens: [...pb.tokens, token] }));
    setSelectedIds(new Set([token.id]));
  }, [addTeam, mutate]);

  const updateTokens = useCallback(
    (ids, patch) => {
      const target = new Set(ids);
      if (!target.size) return;
      mutate((pb) => ({
        ...pb,
        tokens: pb.tokens.map((tok) => (target.has(tok.id) ? { ...tok, ...patch } : tok)),
      }));
    },
    [mutate]
  );

  const deleteTokens = useCallback(
    (ids) => {
      const target = new Set(ids);
      if (!target.size) return;
      mutate((pb) => ({ ...pb, tokens: pb.tokens.filter((tok) => !target.has(tok.id)) }));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of target) next.delete(id);
        return next;
      });
    },
    [mutate]
  );

  // ── Drawing ────────────────────────────────────────────────────────────

  const commitDrawing = useCallback(
    (draft) => {
      const d = createDrawing({
        ...draft,
        tStart: bindMarksToTime ? Number(timeRef.current.toFixed(2)) : 0,
      });
      mutate((pb) => ({ ...pb, drawings: [...pb.drawings, d] }));
    },
    [bindMarksToTime, mutate]
  );

  const clearDrawings = useCallback(() => {
    mutate((pb) => ({ ...pb, drawings: [] }));
    setSelectedDrawingId(null);
  }, [mutate]);

  const deleteDrawing = useCallback(
    (id) => {
      if (!id) return;
      mutate((pb) => ({ ...pb, drawings: pb.drawings.filter((d) => d.id !== id) }));
      setSelectedDrawingId((cur) => (cur === id ? null : cur));
    },
    [mutate]
  );

  const updateDrawing = useCallback(
    (id, patch) => {
      if (!id) return;
      mutate((pb) => ({
        ...pb,
        drawings: pb.drawings.map((d) => (d.id === id ? { ...d, ...patch } : d)),
      }));
    },
    [mutate]
  );

  const editDrawingText = useCallback(
    (id) => {
      const mark = playbookRef.current.drawings.find((d) => d.id === id);
      if (!mark) return;
      // eslint-disable-next-line no-alert
      const next = window.prompt("แก้ข้อความ:", mark.text || "");
      if (next == null) return;
      if (!next.trim()) deleteDrawing(id);
      else updateDrawing(id, { text: next });
    },
    [deleteDrawing, updateDrawing]
  );

  /** Mark a tower as taken, or bring it back. */
  const toggleTower = useCallback(
    (towerId) => {
      mutate((pb) => {
        const down = new Set(pb.downedTowers || []);
        if (down.has(towerId)) down.delete(towerId);
        else down.add(towerId);
        return { ...pb, downedTowers: [...down] };
      });
    },
    [mutate]
  );

  // ── Sides, drafts, storage ─────────────────────────────────────────────

  const swapSides = useCallback(() => {
    mutate((pb) => mirrorPlaybook(pb));
  }, [mutate]);

  const importDraft = useCallback(
    (game, side) => {
      const tokens = tokensFromGame(game, side);
      if (!tokens.length) {
        notify("เกมนี้ไม่มีข้อมูลฮีโร่ในไฟล์");
        return;
      }
      mutate((pb) => ({
        ...pb,
        name: pb.name === "แผนใหม่" ? `ดราฟ ${game.dateStr || ""} vs ${game.competitor || ""}`.trim() : pb.name,
        ourSide: side,
        tokens,
      }));
      setTime(0);
      notify(`โหลดดราฟแล้ว ${tokens.length} ฮีโร่`);
    },
    [mutate, notify]
  );

  /** Read a draft table screenshot and place whatever the parser could read. */
  const importDraftImage = useCallback(
    async (file) => {
      setImageBusy(true);
      setImageError(null);
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error("อ่านไฟล์รูปไม่สำเร็จ"));
          reader.readAsDataURL(file);
        });

        const { rows } = await parseDraftImageByAI(dataUrl);
        const row = rows[0];
        const tokens = row ? tokensFromImageRow(row, playbookRef.current.ourSide) : [];
        if (!tokens.length) {
          setImageError("อ่านชื่อฮีโร่จากรูปนี้ไม่ได้ ลองรูปที่ชัดกว่าเดิม");
          return;
        }

        mutate((pb) => ({ ...pb, name: pb.name === "แผนใหม่" ? describeImageRow(row) : pb.name, tokens }));
        setSelectedIds(new Set());
        setTime(0);
        notify(
          `วางดราฟจากรูปแล้ว ${tokens.length} ฮีโร่${
            rows.length > 1 ? ` (รูปนี้มี ${rows.length} เกม ใช้เกมแรก)` : ""
          } — ตรวจชื่อให้ด้วย`
        );
      } catch (err) {
        console.error(err);
        setImageError(err.message || "อ่านรูปไม่สำเร็จ");
      } finally {
        setImageBusy(false);
      }
    },
    [mutate, notify]
  );

  const handleSave = useCallback(() => {
    const saved = savePlaybook(playbookRef.current);
    if (!saved) {
      notify("บันทึกไม่สำเร็จ — พื้นที่เก็บข้อมูลเต็ม ลองส่งออกเป็นไฟล์แทน");
      return;
    }
    // Saving is not an edit: the history survives it, so you can still undo
    // past the save point.
    replacePlaybook(saved, { saved: true, keepHistory: true });
    setPlaybooks(loadPlaybooks());
    notify("บันทึกแผนแล้ว");
  }, [notify, replacePlaybook]);

  const handleNew = useCallback(() => {
    if (dirty && !window.confirm("แผนปัจจุบันยังไม่ได้บันทึก จะสร้างแผนใหม่เลยไหม?")) return;
    replacePlaybook(createPlaybook(), { saved: true });
    setSelectedIds(new Set());
    setTime(0);
  }, [dirty, replacePlaybook]);

  const handleLoad = useCallback(
    (p) => {
      if (dirty && !window.confirm("แผนปัจจุบันยังไม่ได้บันทึก จะเปิดแผนอื่นเลยไหม?")) return;
      replacePlaybook(p, { saved: true });
      setSelectedIds(new Set());
      setTime(0);
    },
    [dirty, replacePlaybook]
  );

  const handleDelete = useCallback(
    (p) => {
      if (!window.confirm(`ลบแผน “${p.name}” ?`)) return;
      setPlaybooks(deletePlaybook(p.id));
      notify("ลบแผนแล้ว");
    },
    [notify]
  );

  const handleImportFile = useCallback(
    async (file) => {
      try {
        const { playbooks: incoming, terrain: t, corners: c, landmarks: l } = await importFile(file);
        const merged = importPlaybooks(incoming);
        setPlaybooks(merged);
        if (t) mutateTerrain(() => t);
        if (l) {
          setLandmarks(l);
          saveLandmarks(l);
        }
        if (c) {
          setCorners(c);
          saveCorners(c);
        }
        if (incoming[0]) handleLoad(incoming[0]);
        notify(`นำเข้า ${incoming.length} แผนแล้ว`);
      } catch (err) {
        console.error(err);
        notify(err.message || "อ่านไฟล์ไม่สำเร็จ");
      }
    },
    [handleLoad, notify, mutateTerrain]
  );

  // ── Image export ───────────────────────────────────────────────────────

  const buildImage = useCallback(
    () =>
      renderBoardPng({
        svg: boardSvgRef.current,
        mapSrc: `${process.env.PUBLIC_URL}/map.webp`,
        width: IMG_W,
        height: IMG_H,
      }),
    []
  );

  const handleExportImage = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await buildImage();
      const stamp = `${Math.floor(timeRef.current)}s`;
      downloadBlob(blob, `${playbookRef.current.name || "แผน"}-${stamp}.png`);
      notify("บันทึกรูปแล้ว");
    } catch (err) {
      console.error(err);
      notify(err.message || "ส่งออกรูปไม่สำเร็จ");
    } finally {
      setExporting(false);
    }
  }, [buildImage, notify]);

  const handleCopyImage = useCallback(async () => {
    setExporting(true);
    try {
      await copyBlobToClipboard(await buildImage());
      notify("คัดลอกรูปแล้ว — วางในแชตได้เลย");
    } catch (err) {
      console.error(err);
      notify("คัดลอกไม่สำเร็จ ลองกดบันทึกรูปแทน");
    } finally {
      setExporting(false);
    }
  }, [buildImage, notify]);

  // ── Phases ─────────────────────────────────────────────────────────────

  const handleAddPhase = useCallback(() => {
    // eslint-disable-next-line no-alert
    const label = window.prompt("ชื่อจังหวะนี้:", "");
    if (label == null) return;
    const at = Number(timeRef.current.toFixed(2));
    mutate((pb) => ({ ...pb, phases: addPhase(pb.phases || [], at, label) }));
  }, [mutate]);

  const handleRemovePhase = useCallback(
    (id) => {
      mutate((pb) => ({ ...pb, phases: removePhase(pb.phases || [], id) }));
    },
    [mutate]
  );

  // ── AI review ──────────────────────────────────────────────────────────

  const handleReview = useCallback(
    async (question) => {
      setReviewBusy(true);
      setReviewError(null);
      try {
        // Geometry and sight-lines are resolved here, not by the model.
        const plan = summarizePlan({
          playbook: playbookRef.current,
          terrain: terrainRef.current,
          towerVision: true,
          landmarks,
        });
        setReview(await reviewTacticByAI(plan, question));
      } catch (err) {
        console.error(err);
        setReviewError(err.message || "ตรวจแผนไม่สำเร็จ");
      } finally {
        setReviewBusy(false);
      }
    },
    [landmarks]
  );

  // ── Terrain / calibration ──────────────────────────────────────────────

  // Landmarks sit with the map corners rather than the document: they describe
  // the map, not the plan, and a drag fires continuously — so like corners they
  // stay out of the undo stack and lean on their reset button.
  const handleLandmarkChange = useCallback((anchorId, point) => {
    setLandmarks((prev) => {
      const next = { ...prev, [anchorId]: point };
      saveLandmarks(next);
      return next;
    });
  }, []);

  const handleCornerChange = useCallback((key, value) => {
    setCorners((prev) => {
      const next = { ...prev, [key]: value };
      saveCorners(next);
      return next;
    });
  }, []);

  // ── Keyboard ───────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      // A dialog owns the keyboard while it is open, otherwise Delete would
      // quietly remove the tokens behind it and Space would start the clip.
      if (pendingHero || scoutOpen) return;

      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();

      if (mod && key === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (mod && key === "y") {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key === " ") {
        e.preventDefault();
        setPlaying((p) => !p);
        return;
      }
      if (mod && key === "a") {
        e.preventDefault();
        setSelectedDrawingId(null);
        setSelectedIds(new Set(playbookRef.current.tokens.map((tk) => tk.id)));
        return;
      }
      if (mod) return; // leave the rest of the browser's shortcuts alone

      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedIds.size) {
          e.preventDefault();
          deleteTokens([...selectedIds]);
        } else if (selectedDrawingId) {
          e.preventDefault();
          deleteDrawing(selectedDrawingId);
        }
        return;
      }

      // Arrow keys nudge the selection, same auto-key as a drag. Shift for a
      // coarse step, so you can both fine-tune a ward and shift a whole squad.
      if (e.key.startsWith("Arrow") && selectedIds.size) {
        e.preventDefault();
        const stepSize = e.shiftKey ? 0.02 : 0.004;
        const dx = (e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0) * stepSize;
        const dy = (e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0) * stepSize;
        nudgeSelection(dx, dy);
        return;
      }

      if (TOOL_KEYS[key]) {
        setTool(TOOL_KEYS[key]);
        setCalibrating(false);
        setLandmarkMode(false);
        return;
      }

      if (e.key === "Escape") {
        setTool("select");
        setCalibrating(false);
        setLandmarkMode(false);
        setSelectedIds(new Set());
        setSelectedDrawingId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, selectedIds, selectedDrawingId, deleteTokens, deleteDrawing, nudgeSelection, pendingHero, scoutOpen]);

  // Shrinking the clip must not strand the playhead past its end.
  useEffect(() => {
    if (timeRef.current > duration) {
      timeRef.current = duration;
      setTime(duration);
    }
  }, [duration]);

  const usedHeroes = useMemo(
    () => new Set(playbook.tokens.map((tk) => tk.heroName).filter(Boolean)),
    [playbook.tokens]
  );

  const selectedMark =
    playbook.drawings.find((d) => d.id === selectedDrawingId) || null;
  const downedCount = playbook.downedTowers?.length || 0;

  const selectedTokens = useMemo(
    () => playbook.tokens.filter((tk) => selectedIds.has(tk.id)),
    [playbook.tokens, selectedIds]
  );
  // The inspector edits the whole selection; a single token is just the N=1
  // case, and the first one seeds the controls that need a current value.
  const lead = selectedTokens[0] || null;
  const selectedIdList = useMemo(() => selectedTokens.map((tk) => tk.id), [selectedTokens]);

  const extent = playbookExtent(playbook);

  // The board is the point of the page, so below laptop width it takes the full
  // row and the two rails drop underneath it — side by side on a tablet, stacked
  // on a phone. `order` does the reshuffling so the DOM keeps reading
  // left-rail / board / right-rail.
  const wide = useMediaQuery("(min-width: 1180px)");
  const roomy = useMediaQuery("(min-width: 760px)");

  const layout = wide
    ? {
        columns: "minmax(240px, 280px) minmax(420px, 1fr) minmax(250px, 300px)",
        board: { order: 2 },
        left: { order: 1 },
        right: { order: 3 },
      }
    : {
        columns: roomy ? "1fr 1fr" : "1fr",
        board: { order: 1, gridColumn: "1 / -1" },
        left: { order: 2 },
        right: { order: 3 },
      };

  const railStyle = { display: "grid", gap: 12, alignContent: "start", minWidth: 0 };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        color: T.ink,
        fontFamily: T.font,
        padding: "14px 16px 40px",
      }}
    >
      {/* Top bar */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        <Btn tone="ghost" onClick={() => navigate("/dashboard")}>← กลับ</Btn>
        <h1
          style={{
            margin: 0,
            fontSize: 19,
            fontWeight: 600,
            letterSpacing: ".02em",
          }}
        >
          Sucrose Tactic
          <span style={{ color: T.faint, fontSize: 12.5, fontWeight: 400, marginLeft: 10 }}>
            กระดานวางแผน · จำลองการมองเห็น · อนิเมชั่น
          </span>
        </h1>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: 12.5,
              color: T.dim,
              padding: "5px 10px",
              borderRadius: 999,
              border: `1px solid ${playbook.ourSide === "blue" ? TEAM_COLORS.blue : TEAM_COLORS.red}55`,
              background: `${playbook.ourSide === "blue" ? TEAM_COLORS.blue : TEAM_COLORS.red}1a`,
            }}
          >
            เราอยู่ฝั่ง{playbook.ourSide === "blue" ? "บลู" : "เรด"}
          </span>
          <Btn
            onClick={() => openScout("played")}
            title="สถิติของเราและของคู่แข่งจาก log ทุกเกม"
          >
            📊 สถิติทีม
          </Btn>
          <Btn tone="accent" onClick={swapSides} title="พลิกทั้งกระดาน 180 องศา">
            ⇄ สลับฝั่ง
          </Btn>
        </div>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: layout.columns,
          gap: 12,
          alignItems: "start",
        }}
      >
        {/* ── Left rail ─────────────────────────────────────────────── */}
        <div style={{ ...railStyle, ...layout.left }}>
          {/* Shelf and scouting are one panel: the tile you would place is the
              tile whose record you want to read. */}
          <HeroPickerPanel
            addTeam={addTeam}
            onAddTeamChange={setAddTeam}
            onPickHero={setPendingHero}
            usedHeroes={usedHeroes}
            laneIndex={laneIndex}
            teams={scoutTeams}
            team={scoutTeam}
            onTeamChange={setScoutTeam}
            record={scoutRecord}
            statsOf={statsOf}
            poolNote={
              seasonLoading
                ? "กำลังโหลด log ทั้งซีซั่น…"
                : scoutGames === seasonGames
                ? `log ทั้งซีซั่น ${seasonGames.length.toLocaleString()} เกม`
                : `ชีทนี้ ${(games || NO_GAMES).length} เกม`
            }
            onWantStats={() => setWantStats(true)}
            onOpenSheet={games?.length ? openScout : undefined}
            onOpenHero={(name, lane) => openScout("played", name, lane)}
          />
          <DraftImportPanel
            games={games}
            onImport={importDraft}
            onImportImage={importDraftImage}
            imageBusy={imageBusy}
            imageError={imageError}
          />
        </div>

        {/* ── Board ─────────────────────────────────────────────────── */}
        <div style={{ display: "grid", gap: 12, minWidth: 0, ...layout.board }}>
          {/* Toolbar */}
          <div
            style={{
              background: T.panel,
              border: `1px solid ${T.line}`,
              borderRadius: 12,
              padding: 10,
              display: "grid",
              gap: 9,
            }}
          >
            <Row gap={5}>
              {TOOLS.map((tl) => (
                <Btn
                  key={tl.id}
                  active={tool === tl.id && !calibrating && !landmarkMode}
                  onClick={() => {
                    setTool(tl.id);
                              setCalibrating(false);
                    setLandmarkMode(false);
                  }}
                  title={tl.label}
                  style={{ padding: "6px 9px" }}
                >
                  <span style={{ marginRight: 5 }}>{tl.icon}</span>
                  {tl.label}
                </Btn>
              ))}
            </Row>

            <Row gap={8}>
              <Row gap={4}>
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setDrawColor(c)}
                    title={c}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: c,
                      border: drawColor === c ? "2px solid #fff" : "1px solid rgba(255,255,255,0.2)",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  />
                ))}
              </Row>

              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 11.5, color: T.faint }}>หนา</span>
                <input
                  type="range"
                  min={2}
                  max={14}
                  value={drawWidth}
                  onChange={(e) => setDrawWidth(Number(e.target.value))}
                  style={{ width: 84, accentColor: T.accent }}
                />
              </span>

              <Toggle label="ผูกมาร์กกับเวลา" checked={bindMarksToTime} onChange={setBindMarksToTime} />
              <Toggle label="เส้นทางเดิน" checked={showPaths} onChange={setShowPaths} />
              <Toggle label="ป้อม/จุดสำคัญ" checked={showLandmarks} onChange={setShowLandmarks} />

              <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
                <Btn tone="ghost" onClick={undo} title="Ctrl+Z">↶ ย้อนกลับ</Btn>
                <Btn tone="ghost" onClick={redo} title="Ctrl+Shift+Z">↷ ทำซ้ำ</Btn>
                <Btn tone="danger" onClick={clearDrawings} disabled={!playbook.drawings.length}>
                  ล้างเส้นวาด
                </Btn>
                <Btn tone="ghost" onClick={addWard}>+ วอร์ด</Btn>
              </span>
            </Row>
          </div>

          <TacticCanvas
            playbook={playbook}
            projector={projector}
            time={time}
            tool={tool}
            drawColor={drawColor}
            drawWidth={drawWidth}
            selectedIds={selectedIds}
            selectedDrawingId={selectedDrawingId}
            showLandmarks={showLandmarks}
            showPaths={showPaths}
            landmarks={landmarks}
            calibrating={calibrating}
            landmarkMode={landmarkMode}
            svgRef={boardSvgRef}
            onTokensMove={handleTokensMove}
            onSelect={selectTokens}
            onSelectDrawing={selectDrawing}
            onEditDrawingText={editDrawingText}
            onToggleTower={toggleTower}
            onLandmarkChange={handleLandmarkChange}
            onCommitDrawing={commitDrawing}
            onCornersChange={handleCornerChange}
          />

          <TacticTimeline
            playbook={playbook}
            time={time}
            onTimeChange={(t) => {
              timeRef.current = t;
              setTime(t);
            }}
            playing={playing}
            onPlayingChange={setPlaying}
            speed={speed}
            onSpeedChange={setSpeed}
            selectedIds={selectedIds}
            onSelectToken={selectTokens}
            onToggleKeyframe={toggleKeyframes}
            onDurationChange={(d) =>
              mutate((pb) => ({ ...pb, duration: Math.max(5, Math.min(300, d)) }))
            }
            onAddPhase={handleAddPhase}
            onRemovePhase={handleRemovePhase}
          />

          {extent > duration && (
            <Row gap={8} style={{ fontSize: 12, color: T.dim }}>
              <span>มีคีย์เฟรมเลยความยาวคลิปไปถึง {extent.toFixed(1)} วิ</span>
              <Btn
                tone="accent"
                style={{ padding: "4px 9px", fontSize: 12 }}
                onClick={() => mutate((pb) => ({ ...pb, duration: Math.ceil(extent) + 2 }))}
              >
                ขยายความยาวให้พอดี
              </Btn>
            </Row>
          )}
        </div>

        {/* ── Right rail ────────────────────────────────────────────── */}
        <div style={{ ...railStyle, ...layout.right }}>
          {selectedMark && (
            <Panel
              title="มาร์กที่เลือก"
              right={
                <Btn
                  tone="danger"
                  onClick={() => deleteDrawing(selectedMark.id)}
                  style={{ padding: "3px 8px", fontSize: 11 }}
                >
                  ลบ
                </Btn>
              }
            >
              <div style={{ fontSize: 12.5, color: T.dim, marginBottom: 9 }}>
                {MARK_LABELS[selectedMark.tool] || selectedMark.tool}
                {selectedMark.tool === "text" && selectedMark.text ? ` · “${selectedMark.text}”` : ""}
              </div>

              {selectedMark.tool === "text" && (
                <Btn
                  onClick={() => editDrawingText(selectedMark.id)}
                  style={{ width: "100%", marginBottom: 10 }}
                >
                  แก้ข้อความ
                </Btn>
              )}

              <Label>สี</Label>
              <Row gap={4} style={{ marginBottom: 10 }}>
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => updateDrawing(selectedMark.id, { color: c })}
                    title={c}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: c,
                      border:
                        selectedMark.color === c
                          ? "2px solid #fff"
                          : "1px solid rgba(255,255,255,0.2)",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  />
                ))}
              </Row>

              <Label>หนา {selectedMark.width}</Label>
              <input
                type="range"
                min={2}
                max={14}
                value={selectedMark.width}
                onChange={(e) =>
                  updateDrawing(selectedMark.id, { width: Number(e.target.value) })
                }
                style={{ width: "100%", accentColor: T.accent }}
              />

              <div style={{ marginTop: 6, fontSize: 11, color: T.faint, lineHeight: 1.6 }}>
                ดับเบิลคลิกข้อความบนแมพเพื่อแก้ได้เลย · กด Delete เพื่อลบ
              </div>
            </Panel>
          )}

          {lead && (
            <Panel
              title={selectedTokens.length > 1 ? `เลือกอยู่ ${selectedTokens.length} ตัว` : "ฮีโร่ที่เลือก"}
              right={
                <Btn
                  tone="danger"
                  onClick={() => deleteTokens(selectedIdList)}
                  style={{ padding: "3px 8px", fontSize: 11 }}
                >
                  ลบ{selectedTokens.length > 1 ? "ทั้งหมด" : ""}
                </Btn>
              }
            >
              <Row gap={9} style={{ marginBottom: 10 }}>
                <Row gap={0} wrap={false} style={{ flexShrink: 0 }}>
                  {selectedTokens.slice(0, 5).map((tok, i) => (
                    <span
                      key={tok.id}
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: `2px solid ${TEAM_COLORS[tok.team]}`,
                        flexShrink: 0,
                        background: "#0b0d12",
                        marginLeft: i ? -14 : 0,
                      }}
                    >
                      {tok.kind === "hero" && (
                        <img
                          src={getHeroImageUrlWithFallback(tok.heroName)}
                          alt={tok.heroName}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      )}
                    </span>
                  ))}
                </Row>
                <span style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14 }}>
                    {selectedTokens.length > 1
                      ? selectedTokens
                          .map((tk) => tk.heroName || tk.label)
                          .slice(0, 3)
                          .join(", ") + (selectedTokens.length > 3 ? " …" : "")
                      : lead.heroName || lead.label}
                  </div>
                  <div style={{ fontSize: 11, color: T.faint }}>
                    {selectedTokens.length > 1
                      ? "แก้พร้อมกันทั้งกลุ่ม · ลากตัวไหนก็ได้เพื่อย้ายทั้งชุด"
                      : `${lead.role || lead.kind} · ${lead.keys.length} คีย์เฟรม`}
                  </div>
                </span>
              </Row>

              <Label>ทีม</Label>
              <Row gap={6} style={{ marginBottom: 10 }}>
                <Btn
                  tone="blue"
                  active={selectedTokens.every((tk) => tk.team === "blue")}
                  onClick={() => updateTokens(selectedIdList, { team: "blue" })}
                  style={{ flex: 1 }}
                >
                  บลู
                </Btn>
                <Btn
                  tone="red"
                  active={selectedTokens.every((tk) => tk.team === "red")}
                  onClick={() => updateTokens(selectedIdList, { team: "red" })}
                  style={{ flex: 1 }}
                >
                  เรด
                </Btn>
              </Row>

            </Panel>
          )}

          <TacticReviewPanel
            review={review}
            busy={reviewBusy}
            error={reviewError}
            tokenCount={playbook.tokens.length}
            onReview={handleReview}
            onClear={() => {
              setReview(null);
              setReviewError(null);
            }}
          />

          <PlaybookPanel
            playbook={playbook}
            playbooks={playbooks}
            dirty={dirty}
            onNameChange={(v) => mutate((pb) => ({ ...pb, name: v }), { record: false })}
            onNotesChange={(v) => mutate((pb) => ({ ...pb, notes: v }), { record: false })}
            onSave={handleSave}
            onNew={handleNew}
            onLoad={handleLoad}
            onDelete={handleDelete}
            onExport={() => exportPlaybookFile(playbook)}
            onExportAll={() => exportAllFile(playbooks, terrain, corners, landmarks)}
            onImportFile={handleImportFile}
            onExportImage={handleExportImage}
            onCopyImage={canCopyImage() ? handleCopyImage : null}
            exporting={exporting}
          />

          <Panel title="ปรับความแม่นยำของแมพ">
            <div style={{ fontSize: 11.5, color: T.faint, lineHeight: 1.7, marginBottom: 9 }}>
              คลิกที่ป้อมบนแมพเพื่อทำเครื่องหมายว่าโดนทุบไปแล้ว — ใช้บอกเงื่อนไขของแผน
              เช่น “แผนนี้ใช้ได้ตอน T1 บนของเขาหายแล้ว”
              {downedCount > 0 && (
                <>
                  {" · "}
                  <span style={{ color: T.accent }}>ตอนนี้ป้อมล้ม {downedCount} ต้น</span>
                </>
              )}
            </div>

            <Label>ปรับจุดสำคัญ</Label>
            <Row gap={6} style={{ marginBottom: 8 }}>
              <Btn
                active={landmarkMode}
                onClick={() => {
                  setLandmarkMode((m) => !m);
                          setCalibrating(false);
                }}
                style={{ flex: 1 }}
              >
                {landmarkMode ? "เสร็จแล้ว" : "ลากจุดสำคัญ"}
              </Btn>
              <Btn
                tone="ghost"
                onClick={() => {
                  setLandmarks(resetLandmarks());
                  notify("คืนค่าจุดสำคัญแล้ว");
                }}
              >
                คืนค่า
              </Btn>
            </Row>
            {landmarkMode && (
              <div style={{ fontSize: 11.5, color: "#ffb182", lineHeight: 1.7, marginBottom: 12 }}>
                ลากวงกลมประไปวางบนจุดจริงบนแมพ · ฝั่งบลูกับฝั่งเรดผูกกันอยู่
                ลากอันเดียวอีกอันขยับตามเอง แมพจะได้ไม่เอียงข้างใดข้างหนึ่ง
              </div>
            )}

            <Label>ปรับมุมแมพ</Label>
            <Row gap={6}>
              <Btn
                active={calibrating}
                onClick={() => {
                  setCalibrating((c) => !c);
                          setLandmarkMode(false);
                }}
                style={{ flex: 1 }}
              >
                {calibrating ? "เสร็จแล้ว" : "ปรับ 4 มุม"}
              </Btn>
              <Btn
                tone="ghost"
                onClick={() => {
                  setCorners(resetCorners());
                  notify("คืนค่ามุมแมพแล้ว");
                }}
              >
                คืนค่า
              </Btn>
            </Row>
            {calibrating && (
              <div style={{ fontSize: 11.5, color: "#ffb182", lineHeight: 1.7, marginTop: 8 }}>
                ลากวงกลมทั้งสี่ให้ตรงมุมสนามจริง แล้วทุกอย่างบนกระดานจะเข้ารูปตาม
              </div>
            )}
          </Panel>
        </div>
      </div>

      {scoutOpen && (
        <TeamScoutDialog
          games={scoutGames}
          team={scoutTeam}
          onTeamChange={setScoutTeam}
          onClose={() => setScoutOpen(false)}
          scope={excelFile ? scoutScope : undefined}
          onScopeChange={excelFile ? setScoutScope : undefined}
          scopeCounts={{ current: (games || []).length, season: seasonGames.length }}
          scopeLoading={seasonLoading || specterLoading}
          teams={scoutTeams}
          scouted={scouted}
          scoutedTotal={specterRows.length}
          source={scoutSource}
          onSourceChange={setScoutSource}
          initialPool={scoutPool}
          initialHero={scoutHero}
          initialLane={scoutLane}
        />
      )}

      {pendingHero && (
        <LanePickerDialog
          heroName={pendingHero}
          team={addTeam}
          suggested={primaryLaneFor(laneIndex, pendingHero)}
          takenLanes={takenLanes}
          laneIndex={laneIndex}
          onPick={(lane) => placeHero(pendingHero, lane, addTeam)}
          onTeamChange={setAddTeam}
          onCancel={() => setPendingHero(null)}
        />
      )}

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 22,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(10,12,17,0.96)",
            border: `1px solid ${T.accent}66`,
            color: T.ink,
            padding: "10px 18px",
            borderRadius: 10,
            fontSize: 13,
            zIndex: 60,
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
